import React               from 'react';
import { observable, Observe }  from 'rewire-core';
import {
  ActionFn,
  Modal,
  WithStyle,
  Form,
  FormView
}                               from 'rewire-ui';
import { Theme }                from '@material-ui/core/styles';
import Typography               from '@material-ui/core/Typography';
import DraggableResizableDialog from '../ui/DraggableResizableDialog';
import { withStyles }           from '../utility/styles';

export class ConfirmationModel extends Modal {
  message:           string | JSX.Element | (() => JSX.Element) = 'Please confirm';
  secondaryMessage?: string | JSX.Element | (() => JSX.Element);
  maxWidth:          'xs' | 'sm' | 'md' | 'lg' = 'xs';
  yesAction?:        ActionFn;
  noAction?:         ActionFn;
  yesCaption:        string;
  yesDisabled:       () => boolean;
  form?:             Form;

  constructor(params: any) {
    super(params.title, params.open);

    const yesAction        = params.yesAction     || this.defaultYesAction;
    const noAction         = params.noAction      || this.defaultNoAction;
    this.yesCaption        = params.yesCaption    || 'Yes';
    const yesDisabled      = params.yesDisabled   || this.defaultYesDisabled;
    const primaryCaption   = this.yesCaption;
    const secondaryCaption = params.noCaption     || 'No';
    const primaryColour    = params.primaryColour || 'primary';
    const secondaryColour  = params.secondaryColour;

    this.action(secondaryCaption, () => (noAction?.()),  { color: secondaryColour });
    this.action(primaryCaption,   () => (yesAction?.()), { color: primaryColour, variant: 'contained', disabled: (() => (yesDisabled?.())) });
  }

  load(message: string | JSX.Element | (() => JSX.Element), params?: any) {
    this.message          = message;
    this.secondaryMessage = params.secondaryMessage;
    this.yesAction        = params.yesAction;
    this.noAction         = params.noAction;
    this.yesDisabled      = params.yesDisabled;
    this.maxWidth         = params.maxWidth ?? 'xs';
    this.state.title      = params.title ?? 'Confirm';
    this.form             = params.form;
    super.open();
  }

  defaultYesAction: ActionFn = async () => {
    if (this.yesAction) {
      return await this.yesAction();
    }
    return true;
  };

  defaultNoAction: ActionFn = async () => {
    if (this.noAction) {
      return await this.noAction();
    }
    return true;
  };

  defaultYesDisabled = () => {
    if (this.yesDisabled) {
      return this.yesDisabled();
    }
    return false;
  };
}

export const confirmationModel = observable(new ConfirmationModel({ title: 'Confirmation Dialog', open: false }));

const styles = (_theme: Theme) => ({
  message: {
    whiteSpace: 'pre-line',
  },
  secondaryMessage: {
    marginTop: '10px',
  },
});

interface IConfirmationProps {
  viewModel: ConfirmationModel;
}

type ConfirmationProps = WithStyle<ReturnType<typeof styles>, IConfirmationProps>;

class ConfirmationDialog extends React.Component<ConfirmationProps> {
  render() {
    return <Observe render={() => {
      const { classes, viewModel }                                    = this.props;
      const { maxWidth, message, secondaryMessage, form, yesCaption } = viewModel;
      let MessageDisplay            = message;
      let SecondaryMessageDisplay   = secondaryMessage;
      MessageDisplay                = typeof MessageDisplay === 'function' ? <MessageDisplay /> : MessageDisplay;
      SecondaryMessageDisplay       = typeof SecondaryMessageDisplay === 'function' ? <SecondaryMessageDisplay /> : SecondaryMessageDisplay;

      return (
        <DraggableResizableDialog defaultWidth={maxWidth} DialogProps={{dialog: viewModel}} DraggableResizableBoxProps={{enableResizing: false}}>
          <div style={{ textAlign: 'center', margin: 16 }}>
            <Typography className={classes.message} variant='h6'>{MessageDisplay}</Typography>
            {SecondaryMessageDisplay && <Typography className={classes.secondaryMessage} variant='subtitle1'>{SecondaryMessageDisplay}</Typography> }
          </div>
          {!!form &&
            <FormView form={form} onSubmit={confirmationModel.actionFn(yesCaption)} >
              <>{form.fields.map((field) =>
                <div key={field.name} className='content'>
                  <field.Editor className='span1' />
                </div>
              )}
              </>
            </FormView>
          }
        </DraggableResizableDialog>
      );
    }} />;
  }
}

export default withStyles<IConfirmationProps>(styles, ConfirmationDialog);
